import { DatePipe, isPlatformBrowser } from "@angular/common";
import { ChangeDetectorRef, Inject, Injector, PLATFORM_ID, Pipe, PipeTransform } from "@angular/core";
import { TimeagoClock, TimeagoCustomFormatter, TimeagoFormatter, TimeagoIntl, TimeagoPipe } from "ngx-timeago";

@Pipe({
  name: "timeagoWithFormat",
  pure: false
})
export class TimeagoWithFormatPipe implements PipeTransform {
  private pipeCache = new Map<string, TimeagoPipe>();

  // Map AstroBin's language codes to ngx-timeago's language codes
  private langCodeMapping: Record<string, string> = {
    "zh-hans": "zh-CN", // Map Simplified Chinese
    "zh-hant": "zh-TW" // Map Traditional Chinese
  };

  private datePipe: DatePipe | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private injector: Injector,
    private cd: ChangeDetectorRef,
    private clock: TimeagoClock
  ) {}

  transform(
    value: Date | string | number,
    lang: string = "en",
    format: "short" | "long" = "long",
    maxPastDays = 30
  ): string {
    // Convert value to Date object if it's not already
    const dateValue = value instanceof Date ? value : new Date(value);

    // Calculate days difference
    const now = new Date();
    const diffMs = now.getTime() - dateValue.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffMinutes = diffMs / (1000 * 60);

    // Create DatePipe with the current language if we don't have one yet
    if (!this.datePipe) {
      this.datePipe = new DatePipe(lang);
    }

    // If the date is older than maxPastDays, use date format instead of timeago
    if (diffDays > maxPastDays) {
      // Format date based on language
      // Format: 'MMM d, y' (e.g., "Mar 15, 2025")
      return this.datePipe.transform(dateValue, "MMM d, y", undefined, lang) || "";
    }

    // In SSR mode, return a simple static relative time format
    if (!isPlatformBrowser(this.platformId)) {
      if (diffMinutes < 1) {
        return "just now";
      } else if (diffMinutes < 60) {
        const minutes = Math.floor(diffMinutes);
        return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
      } else if (diffHours < 24) {
        const hours = Math.floor(diffHours);
        return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
      } else {
        const days = Math.floor(diffDays);
        return `${days} day${days !== 1 ? "s" : ""} ago`;
      }
    }

    // Otherwise use timeago as normal
    // Map AstroBin's language code to ngx-timeago's language code if needed
    const mappedLang = this.langCodeMapping[lang.toLowerCase()] || lang;

    const key = `TIMEAGO_${mappedLang.toUpperCase()}_${format.toUpperCase()}`;
    let pipe = this.pipeCache.get(key);

    if (!pipe) {
      try {
        // First try to get the exact language and format
        const intl = this.injector.get<TimeagoIntl>(key as any);
        const formatter = new TimeagoCustomFormatter(intl);
        pipe = new TimeagoPipe(intl, this.cd, formatter, this.clock);
        this.pipeCache.set(key, pipe);
      } catch (e) {
        // If format is 'short' and not available, try to use 'long' format for the same language
        if (format === "short") {
          const longKey = `TIMEAGO_${mappedLang.toUpperCase()}_LONG`;
          try {
            console.warn(`Missing TimeagoIntl for key: ${key}. Trying long format for this language: ${longKey}`);
            const longIntl = this.injector.get<TimeagoIntl>(longKey as any);
            const longFormatter = new TimeagoCustomFormatter(longIntl);
            pipe = new TimeagoPipe(longIntl, this.cd, longFormatter, this.clock);
            this.pipeCache.set(key, pipe);
          } catch (e2) {
            // If long format is also not available, fall back to default
            console.warn(`Missing TimeagoIntl for key: ${longKey}. Falling back to default.`);
            pipe = new TimeagoPipe(
              this.injector.get(TimeagoIntl),
              this.cd,
              this.injector.get(TimeagoFormatter),
              this.clock
            );
            this.pipeCache.set(key, pipe);
          }
        } else {
          // For long format or any other format, just fall back to default
          console.warn(`Missing TimeagoIntl for key: ${key}. Falling back to default.`);
          pipe = new TimeagoPipe(
            this.injector.get(TimeagoIntl),
            this.cd,
            this.injector.get(TimeagoFormatter),
            this.clock
          );
          this.pipeCache.set(key, pipe);
        }
      }
    }

    return pipe.transform(dateValue);
  }
}
